import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import userStatusService from '../services/userStatusService';
import { logger } from '../utils/logger';
import { env } from '../env.config';
import { StorageUtils } from '../utils/StorageUtils';

/**
 * Hook pour gérer automatiquement la disponibilité du médecin
 * Met à jour le statut à AVAILABLE quand le médecin se connecte
 * Met à jour le statut à OFFLINE quand le médecin se déconnecte
 */
export function useAutoAvailability() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id || user.role !== 'DOCTOR') {
      return;
    }

    const markAvailability = async (status: 'AVAILABLE' | 'BUSY' | 'OFFLINE') => {
      try {
        await userStatusService.updateAvailabilityStatus(status);
        logger.debug(`✅ [useAutoAvailability] Médecin ${user.id} disponibilité: ${status}`);
      } catch (error) {
        logger.error('❌ [useAutoAvailability] Erreur mise à jour disponibilité:', error);
      }
    };

    const sendBeaconOffline = () => {
      try {
        const token = StorageUtils.getToken();
        const payload = JSON.stringify({ availabilityStatus: 'OFFLINE' });
        
        // Utilisation de fetch avec keepalive au lieu de sendBeacon 
        // car sendBeacon ne supporte pas l'envoi du header Authorization requis par le backend
        fetch(`${env.API_BASE_URL}/user-status/availability`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: payload,
          keepalive: true
        }).catch(err => {
          // Échec silencieux car on quitte la page
          console.warn('[useAutoAvailability] Fetch keepalive failed', err);
        });
      } catch (error) {
        logger.error('❌ [useAutoAvailability] Erreur préparation offline:', error);
      }
    };

    const handleBeforeUnload = () => {
      userStatusService.setUserOffline('DOCTOR');
      sendBeaconOffline();
    };

    userStatusService.setUserOnline('DOCTOR');
    void markAvailability('AVAILABLE');

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      userStatusService.setUserOffline('DOCTOR');
      void markAvailability('OFFLINE');
    };
  }, [user?.id, user?.role]);
}
